import { useEffect, useState, useMemo } from "react";
import { II } from "../../components/common/Icons";
import { R } from "../../utils/helpers";
import HrModal from "./HrModal";
import api from "../../services/api";
import { useHRData } from "../../context/HRContext";
import T from "../../assets/styles/theme";
import "../../assets/styles/HR/AdvancesPg.css";

// ── helpers ──
const _mKey = (d) => {
  if (!d) return null;
  try { const dt=new Date(d); if(isNaN(dt)) return null; return `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,"0")}`; } catch { return null; }
};
const _mLabel = (k) => {
  if (!k) return "";
  const [y,m] = k.split("-");
  return new Date(Number(y),Number(m)-1,1).toLocaleDateString("en-IN",{month:"long",year:"numeric"});
};
const _fmt = (d) => { try { return d ? new Date(d).toLocaleDateString("en-IN",{day:"2-digit",month:"short",year:"numeric"}) : "—"; } catch { return "—"; }};

function exportAdvCSV(rows, filename) {
  const headers = ["Advance ID","Employee","Amount (INR)","Type","EMI (INR)","Months","Remaining","Mgr Approval","Status","Reason","Requested On"];
  const lines = [headers.join(",")];
  rows.forEach(a => {
    const raw = String(a.rawAmt||a.amt||"").replace(/[₹,]/g,"");
    const emi = String(a.emi||"").replace(/[₹,]/g,"");
    const rem = String(a.remaining||"").replace(/[₹,]/g,"");
    lines.push([
      a.id, a.emp, raw, a.type, emi, a.months||"", rem,
      a.mgrStatus||"", a.status||"", a.reason||"",
      _fmt(a.createdAt)
    ].map(v=>`"${String(v||"").replace(/"/g,'""')}"`).join(","));
  });
  const blob = new Blob([lines.join("\n")],{type:"text/csv;charset=utf-8;"});
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href=url; a.download=filename; a.click();
  URL.revokeObjectURL(url);
}

const STATUS_COLORS = {
  draft:    { bg:"#f3f4f6", color:"#374151" },
  pending:  { bg:"#fef3c7", color:"#d97706" },
  approved: { bg:"#dcfce7", color:"#16a34a" },
  rejected: { bg:"#fef2f2", color:"#dc2626" },
  disbursed:{ bg:"#eff6ff", color:"#2563eb" },
};

function AdvancesPg() {
  const { advances, hrEmployees, refreshHRData } = useHRData();
  const [advList, setAdvList] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [suc, setSuc] = useState("");
  const [form, setForm] = useState({ emp:"", empUserId:"", amt:"", type:"EMI", months:"1", reason:"" });

  // Download states
  const [monthFilter, setMonthFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showMonthDrop, setShowMonthDrop] = useState(false);

  useEffect(() => { if (advances) setAdvList(advances); }, [advances]);

  const flash = (msg) => { setSuc(msg); setTimeout(() => setSuc(""), 4000); };

  const handleEmpSelect = (e) => {
    const v = e.target.value;
    if (!v) { setForm({...form, emp:"", empUserId:""}); return; }
    const [n, uid] = v.split("||");
    setForm({...form, emp:n||"", empUserId:uid||""});
  };

  const createAdv = async () => {
    if (!form.emp || !form.amt) return;
    try {
      const amount = parseInt(form.amt.replace(/,/g,""), 10);
      const resp = await api.requestAdvance({
        amount, reason: form.reason,
        userId: form.empUserId ? parseInt(form.empUserId,10) : undefined,
        empName: form.emp, type: form.type,
        months: parseInt(form.months,10)||1
      });
      const newAdv = {
        id: resp.advId||`ADV-${String(advList.length+1).padStart(2,"0")}`,
        emp: form.emp, rawAmt: amount,
        amt: String(amount.toLocaleString("en-IN")),
        type: form.type,
        emi: form.type==="EMI" ? String(Math.round(amount/parseInt(form.months||"1",10))) : "-",
        months: parseInt(form.months,10)||1,
        status:"draft", remaining:String(amount.toLocaleString("en-IN")),
        mgrStatus:"pending", reason:form.reason, sentToMgr:false,
        createdAt: new Date().toISOString()
      };
      setAdvList([newAdv,...advList]);
      setShowCreate(false);
      setForm({emp:"", empUserId:"", amt:"", type:"EMI", months:"1", reason:""});
      flash("Salary advance request created.");
      refreshHRData();
    } catch(e) { alert("Error: "+e.message); }
  };

  const sendToMgr = async (id) => {
    try {
      await api.advanceAction(id,"send_to_mgr");
      setAdvList(advList.map(a=>a.id===id?{...a,status:"pending",sentToMgr:true,mgrStatus:"pending"}:a));
      flash("Advance request sent to manager.");
    } catch(e) { alert("Error: "+e.message); }
  };

  const hrFinalize = async (id, approved) => {
    try {
      await api.advanceAction(id, approved?"hr_approved":"hr_rejected");
      setAdvList(advList.map(a=>a.id===id?{...a,status:approved?"approved":"rejected"}:a));
      flash(`Advance ${approved?"approved":"rejected"} by HR.`);
    } catch(e) { alert("Error: "+e.message); }
  };

  // ── derived ──
  const STATUS_OPTS = ["all","draft","pending","approved","rejected","disbursed"];

  const counts = useMemo(() => {
    const c = {all:advList.length};
    STATUS_OPTS.slice(1).forEach(s => { c[s] = advList.filter(a=>a.status===s).length; });
    return c;
  },[advList]);

  const availMonths = useMemo(() => {
    const set = new Set();
    advList.forEach(a=>{ const k=_mKey(a.createdAt); if(k) set.add(k); });
    return ["all",...Array.from(set).sort((a,b)=>b.localeCompare(a))];
  },[advList]);

  const displayList = useMemo(() => {
    let list = advList;
    if (statusFilter !== "all") list = list.filter(a=>a.status===statusFilter);
    if (monthFilter !== "all")  list = list.filter(a=>_mKey(a.createdAt)===monthFilter);
    return list;
  },[advList, statusFilter, monthFilter]);

  const csvName = () => {
    const p = ["advances"];
    if (statusFilter!=="all") p.push(statusFilter);
    if (monthFilter!=="all")  p.push(monthFilter);
    return p.join("_")+".csv";
  };

  return (
    <div>
      {/* ── Top bar ── */}
      <div className="advancespg-inline-1">
        <div className="sec advancespg-inline-2">Salary Advances</div>
        <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>

          {/* Monthly CSV dropdown */}
          <div style={{position:"relative"}}>
            <button onClick={()=>setShowMonthDrop(v=>!v)}
              style={{display:"flex",alignItems:"center",gap:5,padding:"7px 12px",
                background:"#fff",border:"1px solid #e2e8f0",borderRadius:8,
                fontWeight:600,fontSize:12,color:"#374151",cursor:"pointer"}}>
              📅 Monthly CSV ▾
            </button>
            {showMonthDrop && (
              <div style={{position:"absolute",right:0,top:"calc(100% + 6px)",background:"#fff",
                border:"1px solid #e2e8f0",borderRadius:10,boxShadow:"0 8px 24px rgba(0,0,0,.14)",
                zIndex:300,minWidth:240,overflow:"hidden"}}>
                <div style={{padding:"8px 12px",fontSize:11,fontWeight:700,color:"#64748b",
                  borderBottom:"1px solid #f1f5f9",background:"#f8fafc"}}>MONTH-WISE DOWNLOAD</div>
                {availMonths.filter(m=>m!=="all").length===0 ? (
                  <div style={{padding:"12px",fontSize:12,color:"#94a3b8"}}>No data yet</div>
                ) : availMonths.filter(m=>m!=="all").map(m=>{
                  const mRows = advList.filter(a=>_mKey(a.createdAt)===m);
                  return (
                    <button key={m}
                      onClick={()=>{ exportAdvCSV(mRows,`advances_${m}.csv`); setShowMonthDrop(false); }}
                      style={{display:"flex",justifyContent:"space-between",alignItems:"center",
                        width:"100%",padding:"10px 14px",border:"none",background:"#fff",
                        cursor:"pointer",fontSize:13,borderBottom:"1px solid #f8fafc",textAlign:"left"}}
                      onMouseEnter={e=>e.currentTarget.style.background="#f0f9ff"}
                      onMouseLeave={e=>e.currentTarget.style.background="#fff"}>
                      <span style={{fontWeight:600,color:"#1e293b"}}>{_mLabel(m)}</span>
                      <span style={{fontSize:11,color:"#94a3b8"}}>{mRows.length} advances</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Download current view */}
          <button onClick={()=>exportAdvCSV(displayList,csvName())}
            style={{display:"flex",alignItems:"center",gap:6,padding:"7px 14px",
              background:"#0f172a",color:"#fff",border:"none",borderRadius:8,
              fontWeight:700,fontSize:12,cursor:"pointer",whiteSpace:"nowrap"}}>
            ⬇ Download CSV {displayList.length!==advList.length?`(${displayList.length})`:""}
          </button>

          <button className="btn btn-s" onClick={()=>setShowCreate(true)}>
            {II("plus",14)} Create Advance
          </button>
        </div>
      </div>

      {suc ? <div className="toast">{II("chk",16,{c:"#2563eb"})} {suc}</div> : null}

      {/* ── Status count badges ── */}
      <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:14}}>
        {STATUS_OPTS.map(s => {
          const sc = STATUS_COLORS[s] || {bg:"#f3f4f6",color:"#374151"};
          const active = statusFilter===s;
          return (
            <button key={s} onClick={()=>setStatusFilter(s)} style={{
              display:"flex",flexDirection:"column",alignItems:"center",
              padding:"8px 14px",borderRadius:10,border:"none",cursor:"pointer",minWidth:72,
              background: active ? sc.color : "#f8fafc",
              boxShadow: active ? `0 4px 12px ${sc.color}44` : "0 1px 3px rgba(0,0,0,.06)",
              transition:"all 0.15s"
            }}>
              <span style={{fontSize:20,fontWeight:800,color:active?"#fff":sc.color,lineHeight:1.1}}>
                {counts[s]||0}
              </span>
              <span style={{fontSize:10,fontWeight:600,color:active?"rgba(255,255,255,.85)":"#64748b",
                marginTop:2,textTransform:"capitalize"}}>
                {s==="all"?"All":s}
              </span>
            </button>
          );
        })}
      </div>

      {/* ── Month filter row ── */}
      <div style={{display:"flex",gap:10,flexWrap:"wrap",marginBottom:14,alignItems:"center"}}>
        <span style={{fontSize:12,color:"#64748b",fontWeight:600}}>Month:</span>
        <select value={monthFilter} onChange={e=>setMonthFilter(e.target.value)}
          style={{padding:"7px 10px",border:"1px solid #e2e8f0",borderRadius:8,
            fontSize:12,color:"#374151",background:"#fff",cursor:"pointer"}}>
          <option value="all">All Months</option>
          {availMonths.filter(m=>m!=="all").map(m=>(
            <option key={m} value={m}>{_mLabel(m)}</option>
          ))}
        </select>
        {(statusFilter!=="all"||monthFilter!=="all") && (
          <button onClick={()=>{setStatusFilter("all");setMonthFilter("all");}}
            style={{padding:"6px 10px",border:"1px solid #fecaca",borderRadius:8,
              background:"#fef2f2",color:"#ef4444",fontSize:11,fontWeight:600,cursor:"pointer"}}>
            ✕ Reset
          </button>
        )}
        {(statusFilter!=="all"||monthFilter!=="all") && (
          <span style={{fontSize:12,color:"#64748b"}}>
            Showing <strong style={{color:"#1e293b"}}>{displayList.length}</strong> of {advList.length}
          </span>
        )}
      </div>

      {/* ── Monthly banner ── */}
      {monthFilter!=="all" && (
        <div style={{background:"linear-gradient(135deg,#1e1b4b,#4f46e5)",borderRadius:10,
          padding:"12px 18px",marginBottom:14,display:"flex",justifyContent:"space-between",
          alignItems:"center",color:"#fff"}}>
          <div>
            <div style={{fontWeight:700,fontSize:14}}>📅 {_mLabel(monthFilter)}</div>
            <div style={{fontSize:11,opacity:.8,marginTop:2}}>{displayList.length} advances this month</div>
          </div>
          <div style={{display:"flex",gap:14}}>
            {["approved","pending","draft","rejected"].map(s=>{
              const ct = displayList.filter(a=>a.status===s).length;
              return ct>0?(
                <div key={s} style={{textAlign:"center"}}>
                  <div style={{fontSize:18,fontWeight:800}}>{ct}</div>
                  <div style={{fontSize:9,opacity:.75,textTransform:"capitalize"}}>{s}</div>
                </div>
              ):null;
            })}
          </div>
          <button onClick={()=>exportAdvCSV(displayList,`advances_${monthFilter}.csv`)}
            style={{padding:"7px 14px",background:"rgba(255,255,255,.2)",border:"1px solid rgba(255,255,255,.3)",
              borderRadius:8,color:"#fff",fontWeight:700,fontSize:12,cursor:"pointer"}}>
            ⬇ Download Month CSV
          </button>
        </div>
      )}

      {/* ── Table ── */}
      <div className="cd">
        <div className="cd-h">
          <div className="cd-t">
            {statusFilter==="all"?"Advance Requests":`${statusFilter.charAt(0).toUpperCase()+statusFilter.slice(1)} Advances`}
            {monthFilter!=="all" && <span style={{fontSize:12,color:"#64748b",fontWeight:400,marginLeft:8}}>· {_mLabel(monthFilter)}</span>}
          </div>
          <div className="cd-s">{displayList.length} total</div>
        </div>
        <div className="cd-b advancespg-inline-3">
          <table className="tbl">
            <thead>
              <tr>
                <th>ID</th><th>Employee</th><th>Amount</th><th>Type</th><th>EMI</th>
                <th>Months</th><th>Remaining</th><th>Mgr Approval</th><th>Status</th>
                <th>Requested</th><th>Action</th>
              </tr>
            </thead>
            <tbody>
              {displayList.length===0 ? (
                <tr><td colSpan="11" style={{textAlign:"center",padding:20,color:"#94a3b8"}}>
                  {advList.length===0?"No advance requests found.":"No advances match the current filter."}
                </td></tr>
              ) : displayList.map(a => {
                const canSend = a.status==="draft" && !a.sentToMgr;
                const canHrFinalize = a.sentToMgr && a.mgrStatus==="approved" && a.status==="pending";
                const sc = STATUS_COLORS[a.status]||{bg:"#f3f4f6",color:"#374151"};
                return (
                  <tr key={a.id}>
                    <td className="advancespg-inline-4">{a.id}</td>
                    <td className="advancespg-inline-5">{a.emp}</td>
                    <td style={{fontWeight:600}}>{R}{a.amt}</td>
                    <td>{a.type}</td>
                    <td>{a.emi==="-"?"-":R+a.emi}</td>
                    <td>{a.months}</td>
                    <td>{R}{a.remaining}</td>
                    <td>
                      <span className={"chip "+(a.mgrStatus==="approved"?"chip-ok":a.mgrStatus==="rejected"?"chip-dn":"chip-wn")}>
                        {a.mgrStatus||"pending"}
                      </span>
                    </td>
                    <td>
                      <span style={{background:sc.bg,color:sc.color,padding:"3px 10px",borderRadius:20,fontSize:11,fontWeight:700}}>
                        {a.status}
                      </span>
                    </td>
                    <td style={{fontSize:11,color:"#94a3b8",whiteSpace:"nowrap"}}>{_fmt(a.createdAt)}</td>
                    <td>
                      <div className="advancespg-inline-6">
                        {canSend ? (
                          <button className="btn btn-s advancespg-inline-7" onClick={()=>sendToMgr(a.id)}>
                            {II("send",12)} Send to Mgr
                          </button>
                        ) : canHrFinalize ? (
                          <>
                            <button className="btn btn-s btn-ok advancespg-inline-8" onClick={()=>hrFinalize(a.id,true)}>
                              {II("chk",12)} HR Approve
                            </button>
                            <button className="btn btn-s btn-dn advancespg-inline-9" onClick={()=>hrFinalize(a.id,false)}>
                              {II("x",12)} HR Reject
                            </button>
                          </>
                        ) : (
                          <span style={{"--advancespg-2-color":T.textMuted}} className="advancespg-dyn-2">
                            {a.mgrStatus==="pending"&&a.sentToMgr?"Waiting Manager":"—"}
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <HrModal open={showCreate} onClose={()=>setShowCreate(false)} title="Create Salary Advance Request">
        <div className="fg">
          <label className="fl">Employee Name *</label>
          <select className="fsel" value={form.emp?`${form.emp}||${form.empUserId}`:""} onChange={handleEmpSelect}>
            <option value="">Select employee</option>
            {hrEmployees.map(e=>(
              <option key={e.id} value={`${e.name}||${e.userId||e.user_id||""}`}>
                {e.name} ({e.id})
              </option>
            ))}
          </select>
        </div>
        <div className="fr2">
          <div className="fg">
            <label className="fl">Amount (INR) *</label>
            <input className="fi" value={form.amt} onChange={e=>setForm({...form,amt:e.target.value})} placeholder="e.g. 50,000" />
          </div>
          <div className="fg">
            <label className="fl">Repayment Type</label>
            <select className="fsel" value={form.type} onChange={e=>setForm({...form,type:e.target.value})}>
              <option value="EMI">EMI</option>
              <option value="Full">Full (One-time)</option>
            </select>
          </div>
        </div>
        {form.type==="EMI" && (
          <div className="fg">
            <label className="fl">Number of Months</label>
            <input className="fi" type="number" min="1" max="24" value={form.months} onChange={e=>setForm({...form,months:e.target.value})} />
          </div>
        )}
        <div className="fg">
          <label className="fl">Reason</label>
          <textarea className="fta" value={form.reason} onChange={e=>setForm({...form,reason:e.target.value})} placeholder="Reason for advance request" />
        </div>
        <div className="advancespg-inline-10">
          <button className="btn-o" onClick={()=>setShowCreate(false)}>Cancel</button>
          <button className="btn" onClick={createAdv}>Create Advance</button>
        </div>
      </HrModal>
    </div>
  );
}

export default AdvancesPg;
